// src/components/DataPreviewOverlay.js
import React, { useState, useEffect, useMemo, useRef } from 'react';

const SAFE_PATIENT_DATA = [
  { id: 'demo-1', firstName: 'Sarah', lastName: 'Mitchell', phone: '+6591234567', treatmentType: 'Routine cleaning' },
  { id: 'demo-2', firstName: 'James', lastName: 'Rodriguez', phone: '+6592345678', treatmentType: 'Orthodontic' },
  { id: 'demo-3', firstName: 'Emily', lastName: 'Chen', phone: '+6593456789', treatmentType: 'Root canal' },
  { id: 'demo-4', firstName: 'Michael', lastName: 'Thompson', phone: '+6594567890', treatmentType: 'Crown' },
  { id: 'demo-5', firstName: 'Grace', lastName: 'Williams', phone: '+6595678901', treatmentType: 'Routine cleaning' },
  { id: 'demo-6', firstName: 'David', lastName: 'Park', phone: '+6596789012', treatmentType: 'Whitening' },
  { id: 'demo-7', firstName: 'Linda', lastName: 'Hassan', phone: '+6597890123', treatmentType: 'Implant' },
  { id: 'demo-8', firstName: 'Robert', lastName: 'Kim', phone: '+6598901234', treatmentType: 'Routine cleaning' },
];

export default function DataPreviewOverlay({ onComplete }) {
  const [countdown, setCountdown] = useState(11);
  const [visibleRows, setVisibleRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [highlightCell, setHighlightCell] = useState(null);
  
  const instanceKey = useMemo(() => Date.now(), []);

  // 1. Animate rows appearing
  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index < SAFE_PATIENT_DATA.length) {
        const safePatient = { ...SAFE_PATIENT_DATA[index] };
        if (!safePatient.firstName) safePatient.firstName = 'Patient';
        if (!safePatient.lastName) safePatient.lastName = '';
        setVisibleRows(prev => [...prev, safePatient]);
        index++;
      } else {
        clearInterval(interval);
        setIsLoading(false);
      }
    }, 120);
    return () => clearInterval(interval);
  }, []);

  // 2. TOP-TO-BOTTOM COLUMN CASCADE
  useEffect(() => {
    if (isLoading || visibleRows.length === 0) return;

    let currentCol = 0;
    let currentRow = 0;
    const totalCols = 3;

    const highlightInterval = setInterval(() => {
      if (currentRow >= visibleRows.length) {
        currentRow = 0;
        currentCol++;
      }

      if (currentCol >= totalCols) {
        clearInterval(highlightInterval);
        setHighlightCell(null);
        return;
      }

      setHighlightCell({ rowIndex: currentRow, colIndex: currentCol });
      currentRow++;
    }, 250);

    return () => clearInterval(highlightInterval);
  }, [isLoading, visibleRows.length]);

  // 3. Countdown timer
  useEffect(() => {
    if (isLoading) return;
    if (countdown <= 0) {
      setTimeout(onComplete, 300);
      return;
    }
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, isLoading, onComplete]);

  // Safety loader
  if (!visibleRows || visibleRows.length === 0) {
    return (
      <div key={instanceKey} style={styles.overlay}>
        <div style={styles.glowBg} />
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <div style={styles.loadingText}>Loading patient data...</div>
        </div>
      </div>
    );
  }

  return (
    <div key={instanceKey} style={styles.overlay}>
      <div style={styles.glowBg} />
      
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerTitle}>
            All I need about your Overdue Patients:
          </div>
          <div style={styles.headerSub}>
            {visibleRows.length} of {SAFE_PATIENT_DATA.length} records loaded
          </div>
        </div>
        <div style={styles.timerContainer}>
          <div style={styles.timerLabel}>Auto‑proceeding in</div>
          <div style={styles.timerValue}>{countdown}s</div>
          <div style={styles.timerBar}>
            <div 
              style={{
                ...styles.timerFill,
                width: `${(countdown / 15) * 100}%`,
              }}
            />
          </div>
        </div>
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeadRow}>
              <th style={styles.tableHeadCell}>#</th>
              <th style={styles.tableHeadCell}>Patient</th>
              <th style={styles.tableHeadCell}>Contact</th>
              <th style={styles.tableHeadCell}>Treatment</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((patient, rowIndex) => (
              <tr key={patient.id || `row-${rowIndex}`} style={styles.tableRow}>
                <td style={styles.tableCell}>{rowIndex + 1}</td>
                
                <td 
                  style={{
                    ...styles.tableCell,
                    transition: 'all 0.15s ease-in-out',
                    transform: highlightCell?.rowIndex === rowIndex && highlightCell?.colIndex === 0 
                      ? 'scale(1.06)' 
                      : 'scale(1)',
                    fontWeight: highlightCell?.rowIndex === rowIndex && highlightCell?.colIndex === 0 
                      ? '800' 
                      : '500',
                    color: highlightCell?.rowIndex === rowIndex && highlightCell?.colIndex === 0 
                      ? '#FFFFFF' 
                      : 'rgba(255,255,255,0.8)',
                  }}
                >
                  {patient.firstName || 'Unknown'} {patient.lastName || ''}
                </td>

                <td 
                  style={{
                    ...styles.tableCell,
                    transition: 'all 0.15s ease-in-out',
                    transform: highlightCell?.rowIndex === rowIndex && highlightCell?.colIndex === 1 
                      ? 'scale(1.06)' 
                      : 'scale(1)',
                    fontWeight: highlightCell?.rowIndex === rowIndex && highlightCell?.colIndex === 1 
                      ? '700' 
                      : '400',
                    color: highlightCell?.rowIndex === rowIndex && highlightCell?.colIndex === 1 
                      ? '#FFFFFF' 
                      : 'rgba(255,255,255,0.7)',
                  }}
                >
                  {patient.phone || '—'}
                </td>

                <td style={styles.tableCell}>
                  <span 
                    style={{
                      ...styles.treatmentBadge,
                      transform: highlightCell?.rowIndex === rowIndex && highlightCell?.colIndex === 2 
                        ? 'scale(1.12)' 
                        : 'scale(1)',
                      background: highlightCell?.rowIndex === rowIndex && highlightCell?.colIndex === 2 
                        ? 'rgba(45, 155, 138, 0.35)' 
                        : 'rgba(45, 155, 138, 0.15)',
                      boxShadow: highlightCell?.rowIndex === rowIndex && highlightCell?.colIndex === 2 
                        ? '0 0 20px rgba(45, 155, 138, 0.4)' 
                        : 'none',
                      transition: 'all 0.15s ease-in-out',
                      display: 'inline-block',
                    }}
                  >
                    {patient.treatmentType || 'General'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.centerMessage}>
        <span style={styles.animatedLetter}>O</span>
        <span style={styles.animatedLetter}>n</span>
        <span style={styles.animatedLetter}>l</span>
        <span style={styles.animatedLetter}>y</span>

        <span style={{ width: 14, display: "inline-block" }} />

        <span style={{ ...styles.animatedLetter, ...styles.dataHintHighlight }}>3</span>

        <span style={{ width: 10, display: "inline-block" }} />

        {"Pieces of Data".split("").map((letter, i) => (
          <span
            key={i}
            style={{
              ...styles.animatedLetter,
              ...styles.dataHintHighlight,
              animationDelay: `${(5 + i) * 70}ms`,
            }}
          >
            {letter === " " ? "\u00A0" : letter}
          </span>
        ))}

        <span style={{ width: 10, display: "inline-block" }} />

        {"".split("").map((letter, i) => (
          <span
            key={i}
            style={{
              ...styles.animatedLetter,
              animationDelay: `${(20 + i) * 70}ms`,
            }}
          >
            {letter}
          </span>
        ))}
      </div>

      <div style={styles.footer}>
        <span style={styles.footerText}>
          {countdown > 0 
            ? `You can scroll, zoom, and explore — the demo will start in ${countdown}s`
            : 'Launching demo...'}
        </span>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: '#070E16',
    zIndex: 9998,
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 32px 16px',
    fontFamily: "'Inter', -apple-system, sans-serif",
    animation: 'fadeOverlay 0.4s ease forwards',
  },
  glowBg: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 900,
    height: '80%',
    maxHeight: 700,
    background: 'radial-gradient(circle, rgba(45, 155, 138, 0.08) 0%, rgba(45, 155, 138, 0.02) 40%, rgba(45, 155, 138, 0) 70%)',
    filter: 'blur(60px)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  loadingText: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: 16,
    fontWeight: 400,
    marginTop: 16,
    fontFamily: "'Inter', sans-serif",
  },
  spinner: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.06)',
    borderTopColor: '#2D9B8A',
    animation: 'spin 1s linear infinite',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: 16,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    zIndex: 1,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  headerTitle: {
    fontSize: 48,
    fontWeight: 700,
    color: '#FFFFFF',
    letterSpacing: '-0.5px',
    lineHeight: 1.1,
    background: '#FFFFFF',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  headerSub: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.4)',
    fontWeight: 400,
    marginTop: 4,
  },
  centerMessage: {
    position: 'absolute',
    left: '50%',
    bottom: '170px',
    transform: 'translateX(-50%)',
    zIndex: 5,
    fontSize: 44,
    fontWeight: 500,
    color: 'rgba(255,255,255,.34)',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  },
  animatedLetter: {
    display: 'inline-block',
    transform: 'scale(0.25)',
    opacity: 0,
    animation: 'growLetter 700ms cubic-bezier(.18,.89,.32,1.28) forwards',
  },
  dataHintHighlight: {
    color: '#2D9B8A',
    fontWeight: 700,
  },
  timerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 4,
    minWidth: 120,
  },
  timerLabel: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.3)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: 500,
  },
  timerValue: {
    fontSize: 28,
    fontWeight: 700,
    color: '#2D9B8A',
    letterSpacing: '-0.5px',
    textShadow: '0 0 20px rgba(45, 155, 138, 0.2)',
  },
  timerBar: {
    width: 80,
    height: 2,
    background: 'rgba(255,255,255,0.1)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  timerFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #2D9B8A, #3ABFA8)',
    borderRadius: 2,
    transition: 'width 1s linear',
  },
  tableWrapper: {
    flex: 1,
    overflowY: 'auto',
    marginTop: 16,
    zIndex: 1,
    paddingRight: 4,
    maxWidth: '70%', // Table is 30% smaller
    margin: '0 auto',
    width: '100%',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14, // Normal readable size
  },
  tableHeadRow: {
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  tableHeadCell: {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: "'JetBrains Mono', monospace",
    position: 'sticky',
    top: 0,
    background: '#070E16',
    zIndex: 2,
  },
  tableRow: {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    transition: 'background 0.2s',
    ':hover': {
      background: 'rgba(255,255,255,0.03)',
    },
  },
  tableCell: {
    padding: '10px 14px',
    color: 'rgba(255,255,255,0.8)',
    fontFamily: "'Inter', sans-serif",
    fontSize: 14,
  },
  treatmentBadge: {
    display: 'inline-block',
    padding: '2px 12px',
    background: 'rgba(45, 155, 138, 0.15)',
    color: '#2D9B8A',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500,
    border: '1px solid rgba(45, 155, 138, 0.2)',
  },
  footer: {
    paddingTop: 12,
    borderTop: '1px solid rgba(255,255,255,0.04)',
    textAlign: 'center',
    zIndex: 1,
  },
  footerText: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.2)',
    fontWeight: 400,
    letterSpacing: '0.3px',
  },
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes fadeOverlay {
    0% { opacity: 0; transform: scale(0.98); }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes growLetter {
    0% {
      transform: scale(.2);
      opacity: 0;
    }
    45% {
      transform: scale(1.35);
      opacity: 1;
    }
    70% {
      transform: scale(.92);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);